import { useQuery, useMutation, useQueryClient } from 'react-query';
import { supabase } from '../lib/supabaseClient';

interface UseSearchesOptions {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
}

export const useSearches = (options: UseSearchesOptions = {}) => {
  const {
    page = 1,
    pageSize = 10,
    searchTerm = ''
  } = options;
  
  const queryClient = useQueryClient();

  const {
    data: searchData,
    isLoading,
    isFetching,
    error
  } = useQuery(
    ['searches', { page, pageSize, searchTerm }],
    async () => {
      let query = supabase
        .from('searches')
        .select('id, query, searches, likes, created_at, last_searched_at', { count: 'exact' });

      if (searchTerm) {
        query = query.ilike('query', `%${searchTerm}%`);
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, count, error } = await query
        .order('searches', { ascending: false })
        .order('last_searched_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      return {
        searches: data?.map(search => ({
          id: search.id,
          query: search.query,
          searches: search.searches,
          likes: search.likes || 0,
          createdAt: search.created_at,
          lastSearchedAt: search.last_searched_at
        })) || [],
        totalCount: count || 0
      };
    },
    {
      keepPreviousData: true,
      staleTime: 30000
    }
  );

  const { mutateAsync: deleteSearch } = useMutation(
    async (searchId: string) => {
      // First delete all market search connections
      const { error: marketSearchError } = await supabase
        .from('market_searches')
        .delete()
        .eq('search_id', searchId);

      if (marketSearchError) throw marketSearchError;

      // Delete user likes
      const { error: likesError } = await supabase
        .from('user_likes')
        .delete()
        .eq('search_id', searchId);

      if (likesError) throw likesError;

      // Delete user search history
      const { error: historyError } = await supabase
        .from('user_search_history')
        .delete()
        .eq('search_id', searchId);

      if (historyError) throw historyError;

      // Finally delete the search itself
      const { error: searchError } = await supabase
        .from('searches')
        .delete()
        .eq('id', searchId);

      if (searchError) throw searchError;
    },
    {
      onSuccess: () => {
        // Invalidate all related queries
        queryClient.invalidateQueries('searches');
        queryClient.invalidateQueries(['marketSearches']);
        queryClient.invalidateQueries(['userLikes']);
        queryClient.invalidateQueries(['userHistory']);
      }
    }
  );

  const { mutateAsync: deleteSearches } = useMutation(
    async (ids: string[]) => {
      // First delete all market search connections
      const { error: marketSearchError } = await supabase
        .from('market_searches')
        .delete()
        .in('search_id', ids);

      if (marketSearchError) throw marketSearchError;

      // Delete user likes
      const { error: likesError } = await supabase
        .from('user_likes')
        .delete()
        .in('search_id', ids);

      if (likesError) throw likesError;

      // Delete user search history
      const { error: historyError } = await supabase
        .from('user_search_history')
        .delete()
        .in('search_id', ids);

      if (historyError) throw historyError;

      // Finally delete the searches themselves
      const { error: searchError } = await supabase
        .from('searches')
        .delete()
        .in('id', ids);

      if (searchError) throw searchError;
    },
    {
      onSuccess: () => {
        // Invalidate all related queries
        queryClient.invalidateQueries('searches');
        queryClient.invalidateQueries(['marketSearches']);
        queryClient.invalidateQueries(['userLikes']);
        queryClient.invalidateQueries(['userHistory']);
      }
    }
  );

  return {
    searches: searchData?.searches || [],
    totalCount: searchData?.totalCount || 0,
    isLoading,
    isFetching,
    error,
    deleteSearch,
    deleteSearches
  };
};